import * as querystring from 'querystring';
import * as url from 'url';

import { getToken } from '~/src/dash/api/auth/auth';

interface ITokenObject {
  access_token: string;
  refresh_token?: string;
}
import { getOAuthToken } from '~/src/dash/api/console-api';
import { encodeQuery } from '~/src/dash/api/resource';
import { getApiDomain } from '~/src/dash/resellers/utils/importResellerConfig';
import { TKey } from '~/src/locales/en';

// Rest library to make requests to the API
// Here is an example:
//
// export async function updateRoleForUser(roleID: number, userID: number, publisherID: number): Promise<[IRole | undefined, string | undefined]>  {
//     const reqUrl = createUrl('/role/user', publisherID.toString());
//
//     try {
//         return [(await restPut<IRole>(reqUrl, {
//             roleID,
//             userID,
//         })).parsedBody, undefined];
//     } catch (e) {
//         return [undefined, e];
//     }
// }
//
// Then handle the return like this:
//
// createRole(this.state.newRoleName, parseInt(this.props.session.publisher.ID, 10)).then(
//   ([role: IRole | undefined, err: Error | undefined]) => {
//       if (err !== undefined) {
//           this.setState({
//               snackbarMessage: 'Error during role creation ' + err.toString(),
//               snackbarError: true,
//           });
//       } else {
//           if (role !== undefined) {
//               this.setState({
//                   snackbarMessage: 'Role ' + role.name + ' successfully created!',
//                   snackbarError: false,
//               });
//           }
//           this.getAvailableRoles();
//       }
//   });

const headerDict = {
  'Content-Type': 'application/json',
};

const headerDictGet = {
  'Content-Type': 'application/x-www-form-urlencoded',
};

export enum ResponseType {
  unknown = 0,
  json = 1,
  blob = 2,
}

export interface IHttpResponse<T> extends Response {
  parsedBody?: T;
  blobData?: Blob;
}

/**
 * Standard API error response structure
 */
export interface IApiErrorResponse {
  errorTKey?: TKey;
  success: boolean;
  // Legacy error fields (for backwards compatibility)
  error?: string;
  msg?: string;
}

/**
 * Extended Error type that includes API response details
 */
export type ApiError = Error & {
  status?: number;
  statusText?: string;
  url?: string;
  method?: string;
  responseBody?: IApiErrorResponse;
};

export function createUrl(
  urlPart: string,
  publisherID: string = '',
  version: string = 'v1',
): string {
  const tokenObj = getToken() as ITokenObject;
  const accessToken = tokenObj.access_token;

  return (
    getApiDomain() +
    '/' +
    version +
    '/settings' +
    urlPart +
    '?access_token=' +
    accessToken +
    (publisherID ? `&publisher_id=${publisherID}` : '')
  );
}

export function createUrlPublicaMonitors(
  urlPart: string,
  publisherIDs?: string,
  version: string = 'v1',
): string {
  const tokenObj = getToken() as ITokenObject;
  const accessToken = tokenObj.access_token;

  return (
    getApiDomain() +
    '/' +
    version +
    '/settings' +
    urlPart +
    '?access_token=' +
    accessToken +
    (publisherIDs ? `&publisher_ids=${publisherIDs}` : '')
  );
}

export function createAnalyticsUrl(
  urlPart: string,
  publisherID: string = '',
  version: string = 'v1',
): string {
  const tokenObj = getToken() as ITokenObject;
  const accessToken = tokenObj.access_token;
  return `${getApiDomain()}/${version}/analytics/${urlPart}?access_token=${accessToken}${
    publisherID ? `&publisher_id=${publisherID}` : ''
  }`;
}

export function generateTokenParams(publisherID: string): string {
  const tokenObj = getToken() as ITokenObject;
  const accessToken = tokenObj.access_token;

  return 'access_token=' + accessToken + '&publisher_id=' + publisherID;
}

export function createUrlWithParam(
  urlPart: string,
  publisherID: string,
  id: string,
  idName: string,
): string {
  const tokenObj = getToken() as ITokenObject;
  const accessToken = tokenObj.access_token;

  return (
    getApiDomain() +
    '/v1/settings' +
    urlPart +
    '?access_token=' +
    accessToken +
    '&publisher_id=' +
    publisherID +
    `&${idName}=` +
    id
  );
}

export function addDomainAndTokenToUrl(urlPart: string): string {
  const tokenObj = getToken() as ITokenObject;
  const accessToken = tokenObj.access_token;

  return getApiDomain() + '/v1/settings' + urlPart + '?access_token=' + accessToken;
}

export function addDomainAndTokenToUrlV2(urlPart: string): string {
  const tokenObj = getToken() as ITokenObject;
  const accessToken = tokenObj.access_token;

  return getApiDomain() + '/v2/settings' + urlPart + '?access_token=' + accessToken;
}

export function addDomainAndTokenToAnalyticsURL(urlPart: string): string {
  const tokenObj = getToken() as ITokenObject;
  const accessToken = tokenObj.access_token;

  return getApiDomain() + '/v1/analytics' + urlPart + '?access_token=' + accessToken;
}

export function createURLWithTokenAndCustomPath(urlPart: string): string {
  const accessToken = (getToken() as ITokenObject).access_token;
  return getApiDomain() + '/v1' + urlPart + '?access_token=' + accessToken;
}

export function addQueryParams(url: string, queryParams: { [key: string]: any }): string {
  if (!Object.keys(queryParams).length) {
    return url;
  }
  const params = encodeQuery(queryParams);
  return url + '&' + params;
}

export function retryFunc(cb: (ok: boolean) => any) {
  const token = getToken() as ITokenObject;

  if (token && token.refresh_token) {
    getOAuthToken({
      query: {
        grant_type: 'refresh_token',
        refresh_token: token.refresh_token,
      },
    }).then((json: any) => {
      if (json.error) {
        cb(false);
      } else {
        cb(true);
      }
    });
  } else {
    cb(false);
  }
}

// call the get token func and then mutate the request, put the good token and restart the request
async function retry<T>(
  request: Request,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  init: any,
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  originalError?: Error | unknown,
): Promise<IHttpResponse<T>> {
  return new Promise((resolve, reject) => {
    retryFunc((ret: boolean) => {
      if (ret) {
        // mutate the request to put the good token
        const reqUrl = url.parse(request.url, true);
        const parsedQs = reqUrl.query;
        parsedQs.access_token = (getToken() as ITokenObject).access_token as string | string[]; // update the access token
        const encodedQs = querystring.encode(parsedQs); // encode back the query string

        // rm the query from the url
        let urlWithGoodToken = reqUrl.href.replace(reqUrl.search || '', '');
        // add the query with the good token to the url
        urlWithGoodToken += '?' + encodedQs;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const requestWithNewToken = new Request(urlWithGoodToken, init);

        // restart the request with the good token
        // set "allowRetry - 3rd param" to false to prevent infinite loop
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        resolve(http<T>(requestWithNewToken, undefined, init, false));
      } else {
        // Preserve the original error when token refresh fails
        const refreshError = new Error("can't refresh refresh token");
        if (originalError) {
          // Attach original error information
          if (originalError instanceof Error) {
            // Include more context if it's an HTTP error
            // eslint-disable-next-line
            const hasHttpContext = (originalError as any).status !== undefined;
            // eslint-disable-next-line
            const contextInfo = hasHttpContext
              ? // eslint-disable-next-line
                ` [HTTP ${(originalError as any).status} on ${(originalError as any).method} ${
                  // eslint-disable-next-line
                  (originalError as any).url
                }]`
              : '';

            refreshError.message = `can't refresh refresh token${contextInfo} (original error: ${originalError.message})`;
            refreshError.stack = `${refreshError.stack}\n\nOriginal error:\n${originalError.stack}`;
          } else {
            refreshError.message = `can't refresh refresh token (original error: ${String(
              originalError,
            )})`;
          }
        }
        reject(refreshError);
      }
    });
  });
}

export async function http<T>(
  request: Request,
  responseType: ResponseType = ResponseType.json,
  init: any,
  allowRetry: boolean = true, // if set to true, if the request fail we get a new access token and restart the req with this new token
): Promise<IHttpResponse<T>> {
  let response = undefined as any as IHttpResponse<T>;
  // let start_ms: number;
  // let duration_api_call: number;
  // let duration_parse_response: number;

  try {
    // start_ms = Date.now();
    response = await fetch(request);
  } catch (e) {
    console.error(e);
    // Don't retry if request was aborted
    if (e instanceof Error && e.name === 'AbortError') {
      throw e;
    }
    // retry
    if (allowRetry) {
      return await retry(request, init, e);
    }
    throw new Error(e);
  }

  try {
    // duration_api_call = Date.now() - start_ms;
    // start_ms = Date.now();
    // parse body
    if (responseType === ResponseType.blob) {
      response.blobData = await response?.blob();
    } else if (responseType === ResponseType.json) {
      response.parsedBody = await response?.json();
    } else {
      throw new Error('Cannot parse the response: unknown ResponseType');
    }
    // duration_parse_response = Date.now() - start_ms;
  } catch (ex) {
    throw new Error(ex);
  }
  if (!response.ok) {
    // retry
    if (allowRetry) {
      // eslint-disable-next-line
      const errorMessage =
        // eslint-disable-next-line
        response.parsedBody !== undefined &&
        // eslint-disable-next-line
        (response.parsedBody as unknown as any).error !== undefined
          ? // eslint-disable-next-line
            (response.parsedBody as unknown as any).error
          : response.statusText;

      // Create a more descriptive error with context
      const responseError = new Error(
        `HTTP ${response.status} ${response.statusText}: ${errorMessage} (Request: ${request.method} ${request.url})`,
      );
      // Preserve the original error details in the error object
      // eslint-disable-next-line
      (responseError as any).status = response.status;
      // eslint-disable-next-line
      (responseError as any).statusText = response.statusText;
      // eslint-disable-next-line
      (responseError as any).url = request.url;
      // eslint-disable-next-line
      (responseError as any).method = request.method;
      // eslint-disable-next-line
      (responseError as any).responseBody = response.parsedBody;

      return await retry(request, init, responseError);
    }

    // else read error
    if (response.parsedBody !== undefined) {
      const body = response.parsedBody as unknown as IApiErrorResponse;

      // Prioritize errorTKey (new standard), then fall back to legacy fields
      if (body.errorTKey) {
        // Don't translate here - just pass the key through
        // Translation will happen at the component level where t() is available
        const error = new Error(body.errorTKey) as ApiError;
        error.responseBody = body;
        throw error;
      }

      // Legacy error field support - keep the raw message
      if (body.error) {
        const error = new Error(body.error) as ApiError;
        error.responseBody = body;
        throw error;
      }

      // No errorTKey or error field - use default but preserve any existing data
      const defaultError = new Error('ConsoleErrorMsgUnexpectedError') as ApiError;
      defaultError.responseBody = {
        ...body,
        errorTKey: 'ConsoleErrorMsgUnexpectedError',
      };
      throw defaultError;
    }

    // No parsedBody at all - use default with statusText preserved
    const defaultError = new Error('ConsoleErrorMsgUnexpectedError') as ApiError;
    defaultError.responseBody = {
      errorTKey: 'ConsoleErrorMsgUnexpectedError',
      success: false,
      error: response.statusText, // Preserve statusText as legacy error
    };
    throw defaultError;
  }

  // only track api response timings if request successful
  try {
    // track_api_latency(request.url, duration_api_call, duration_parse_response, 'rest');
  } catch (e) {
    console.warn('error while sending segment.io data: ', e);
  }
  return response;
}

export const defaultRestGetArgs: RequestInit = { method: 'get', headers: headerDictGet };
export async function restGet<T>(
  path: string,
  args: RequestInit = defaultRestGetArgs,
  responseType?: ResponseType,
): Promise<IHttpResponse<T>> {
  return await http<T>(new Request(path, args), responseType, args);
}

export async function restPost<T>(
  path: string,
  body: any,
  args: RequestInit = { method: 'post', body: JSON.stringify(body), headers: headerDict },
  responseType?: ResponseType,
  signal?: AbortSignal,
): Promise<IHttpResponse<T>> {
  const requestArgs = signal ? { ...args, signal } : args;
  return await http<T>(new Request(path, requestArgs), responseType, requestArgs);
}

export async function restPut<T>(
  path: string,
  body: any,
  args: RequestInit = { method: 'put', body: JSON.stringify(body), headers: headerDict },
  responseType?: ResponseType,
): Promise<IHttpResponse<T>> {
  return await http<T>(new Request(path, args), responseType, args);
}

export async function restPatch<T>(
  path: string,
  body: any,
  args: RequestInit = { method: 'PATCH', body: JSON.stringify(body), headers: headerDict },
  responseType?: ResponseType,
): Promise<IHttpResponse<T>> {
  return await http<T>(new Request(path, args), responseType, args);
}

export async function restDelete<T>(
  path: string,
  body: any,
  args: RequestInit = { method: 'DELETE', body: JSON.stringify(body), headers: headerDict },
  responseType?: ResponseType,
): Promise<IHttpResponse<T>> {
  return await http<T>(new Request(path, args), responseType, args);
}
